import React from 'react';
import { useCubeQuery } from '@cubejs-client/react';
import { DateRangePickerValue } from 'ui/MuiDateRangePicker';
import {
    ACTIVE_USERS_FREQUENCY_AUDIENCE_ANALYTICS_MEASURES,
    ACTIVE_USERS_TOTALS_AUDIENCE_ANALYTICS_MEASURES,
    AUDIENCE_FREQUENCY_ANALYTICS_MEASURES,
    AUDIENCE_TOTALS_ANALYTICS_MEASURES,
    EAudienceMeasureType,
    EAudienceMeasureView,
    getAudienceAnalyticsSegmentDimension,
    getAudienceAnalyticsTimeDimensions,
    getAudienceMeasuresListWithRetroData,
    getCubeActiveUsersAnalyticsFilters,
    getCubeAudienceAnalyticsFilters,
    parseAudienceMeasureKey
} from './model';
import { Filter } from '@cubejs-client/core';
import { isDefined } from 'lib/typeguards';
import { differenceInCalendarDays } from 'date-fns';

export function useAudienceAnalyticsQueries(
    selectedMeasure: string,
    selectedLocation: string,
    dateRange: DateRangePickerValue,
    retroDateRange: [Date, Date],
    timeGranularity: string,
    audiencesToCompare: string[]
) {
    const activeUsersFilters = React.useMemo</* readonly DeeplyReadonly<Filter>[]*/ Filter[]>(
        () => getCubeActiveUsersAnalyticsFilters(selectedLocation),
        [selectedLocation]
    );
    const segmentFilters = React.useMemo</* readonly DeeplyReadonly<Filter>[]*/ Filter[]>(() => {
        if (!Array.isArray(audiencesToCompare) || audiencesToCompare.length === 0) {
            return [];
        }
        return getCubeAudienceAnalyticsFilters(selectedLocation, audiencesToCompare);
    }, [audiencesToCompare, selectedLocation]);
    const segmentChartFilters = React.useMemo</* readonly DeeplyReadonly<Filter>[]*/ Filter[]>(() => {
        if (audiencesToCompare?.length < 1) {
            return [];
        }
        return getCubeAudienceAnalyticsFilters(selectedLocation, [...audiencesToCompare]);
    }, [audiencesToCompare, selectedLocation]);
    const [retroStartDate, retroEndDate] = React.useMemo(() => retroDateRange, [retroDateRange]);

    const activeUsersTotalQuery = useCubeQuery({
        measures: ACTIVE_USERS_TOTALS_AUDIENCE_ANALYTICS_MEASURES,
        dimensions: [],
        timeDimensions: getAudienceAnalyticsTimeDimensions(EAudienceMeasureView.ACTIVE_USERS_VIEW, dateRange),
        filters: activeUsersFilters
    });
    const activeUsersFrequencyQuery = useCubeQuery({
        measures: ACTIVE_USERS_FREQUENCY_AUDIENCE_ANALYTICS_MEASURES,
        dimensions: [],
        timeDimensions: getAudienceAnalyticsTimeDimensions(EAudienceMeasureView.ACTIVE_USERS_VIEW, dateRange),
        filters: activeUsersFilters
    });
    const activeUsersTotalChartQuery = useCubeQuery({
        measures: ACTIVE_USERS_TOTALS_AUDIENCE_ANALYTICS_MEASURES,
        dimensions: [],
        timeDimensions: getAudienceAnalyticsTimeDimensions(
            EAudienceMeasureView.ACTIVE_USERS_VIEW,
            dateRange,
            timeGranularity
        ),
        filters: activeUsersFilters
    });
    const activeUsersFrequencyChartQuery = useCubeQuery({
        measures: ACTIVE_USERS_FREQUENCY_AUDIENCE_ANALYTICS_MEASURES,
        dimensions: [],
        timeDimensions: getAudienceAnalyticsTimeDimensions(
            EAudienceMeasureView.ACTIVE_USERS_VIEW,
            dateRange,
            timeGranularity
        ),
        filters: activeUsersFilters
    });
    // do not load retro data if we request more than a year
    const loadRetroData = React.useMemo(
        () => differenceInCalendarDays(retroEndDate, retroStartDate) <= 366,
        [retroEndDate, retroStartDate]
    );
    const activeUsersRetrospectiveTotalQuery = useCubeQuery(
        {
            measures: ACTIVE_USERS_TOTALS_AUDIENCE_ANALYTICS_MEASURES,
            dimensions: [],
            timeDimensions: getAudienceAnalyticsTimeDimensions(EAudienceMeasureView.ACTIVE_USERS_VIEW, {
                startDate: retroStartDate,
                endDate: retroEndDate
            }),
            filters: activeUsersFilters
        },
        { skip: !loadRetroData }
    );
    const activeUsersRetrospectiveFrequencyQuery = useCubeQuery(
        {
            measures: ACTIVE_USERS_FREQUENCY_AUDIENCE_ANALYTICS_MEASURES,
            dimensions: [],
            timeDimensions: getAudienceAnalyticsTimeDimensions(EAudienceMeasureView.ACTIVE_USERS_VIEW, {
                startDate: retroStartDate,
                endDate: retroEndDate
            }),
            filters: activeUsersFilters
        },
        { skip: !loadRetroData }
    );
    const audienceTotalsQuery = useCubeQuery(
        {
            measures: AUDIENCE_TOTALS_ANALYTICS_MEASURES,
            dimensions: [getAudienceAnalyticsSegmentDimension(EAudienceMeasureView.AUDIENCE_VIEW)],
            timeDimensions: getAudienceAnalyticsTimeDimensions(EAudienceMeasureView.AUDIENCE_VIEW, dateRange),
            filters: segmentFilters
        },
        { skip: audiencesToCompare?.length < 1 }
    );
    const audienceFrequencyQuery = useCubeQuery(
        {
            measures: AUDIENCE_FREQUENCY_ANALYTICS_MEASURES,
            dimensions: [getAudienceAnalyticsSegmentDimension(EAudienceMeasureView.AUDIENCE_VIEW)],
            timeDimensions: getAudienceAnalyticsTimeDimensions(EAudienceMeasureView.AUDIENCE_VIEW, dateRange),
            filters: segmentFilters
        },
        { skip: audiencesToCompare?.length < 1 }
    );
    const audienceChartTotalsQuery = useCubeQuery(
        {
            measures: [`${EAudienceMeasureView.AUDIENCE_VIEW}.${selectedMeasure}`],
            dimensions: [getAudienceAnalyticsSegmentDimension(EAudienceMeasureView.AUDIENCE_VIEW)],
            timeDimensions: getAudienceAnalyticsTimeDimensions(
                EAudienceMeasureView.AUDIENCE_VIEW,
                dateRange,
                timeGranularity
            ),
            filters: segmentChartFilters
        },
        { skip: audiencesToCompare?.length < 1 || selectedMeasure === EAudienceMeasureType.FREQUENCY }
    );
    const audienceChartFrequencyQuery = useCubeQuery(
        {
            measures: [`${EAudienceMeasureView.AUDIENCE_VIEW}.${selectedMeasure}`],
            dimensions: [getAudienceAnalyticsSegmentDimension(EAudienceMeasureView.AUDIENCE_VIEW)],
            timeDimensions: getAudienceAnalyticsTimeDimensions(
                EAudienceMeasureView.AUDIENCE_VIEW,
                dateRange,
                timeGranularity
            ),
            filters: segmentChartFilters
        },
        { skip: audiencesToCompare?.length < 1 || selectedMeasure !== EAudienceMeasureType.FREQUENCY }
    );

    const [measureValues, currentMeasuresRecord] = React.useMemo(
        () =>
            getAudienceMeasuresListWithRetroData(
                [activeUsersTotalQuery, activeUsersFrequencyQuery],
                [activeUsersRetrospectiveTotalQuery, activeUsersRetrospectiveFrequencyQuery],
                loadRetroData
            ),
        [
            activeUsersFrequencyQuery,
            activeUsersRetrospectiveFrequencyQuery,
            activeUsersRetrospectiveTotalQuery,
            activeUsersTotalQuery,
            loadRetroData
        ]
    );
    const currentUsersActiveMeasure = React.useMemo(
        () => ({
            value: currentMeasuresRecord[selectedMeasure]?.value,
            loading:
                currentMeasuresRecord[selectedMeasure]?.loading ??
                !isDefined(currentMeasuresRecord[selectedMeasure]?.value)
        }),
        [currentMeasuresRecord, selectedMeasure]
    );
    const audienceMeasureValues = React.useMemo(() => {
        const measuresRecord: Record<string, Partial<Record<EAudienceMeasureType, number>>> = {};
        audienceTotalsQuery.resultSet?.series().forEach(item => {
            item.series.forEach(measureData => {
                const [_, measure] = parseAudienceMeasureKey(item.key);
                measuresRecord[measureData.x] = {
                    ...(measuresRecord[measureData.x] || {}),
                    [measure]: measureData.value || 0
                };
            });
        });
        audienceFrequencyQuery.resultSet?.series().forEach(item => {
            item.series.forEach(measureData => {
                const [_, measure] = parseAudienceMeasureKey(item.key);
                measuresRecord[measureData.x] = {
                    ...(measuresRecord[measureData.x] || {}),
                    [measure]: measureData.value || 0
                };
            });
        });
        return measuresRecord;
    }, [audienceFrequencyQuery.resultSet, audienceTotalsQuery.resultSet]);
    const error = React.useMemo(
        () =>
            activeUsersTotalQuery.error ||
            activeUsersFrequencyQuery.error ||
            audienceTotalsQuery.error ||
            audienceFrequencyQuery.error,
        [
            activeUsersFrequencyQuery.error,
            activeUsersTotalQuery.error,
            audienceFrequencyQuery.error,
            audienceTotalsQuery.error
        ]
    );
    const activeUsersChartQueries = React.useMemo(
        () => [activeUsersTotalChartQuery, activeUsersFrequencyChartQuery],
        [activeUsersFrequencyChartQuery, activeUsersTotalChartQuery]
    );

    const audienceChartQueries = React.useMemo(
        () => [audienceChartTotalsQuery, audienceChartFrequencyQuery],
        [audienceChartFrequencyQuery, audienceChartTotalsQuery]
    );
    const audiencesDataLoading = React.useMemo(
        () =>
            selectedMeasure === EAudienceMeasureType.FREQUENCY
                ? audienceFrequencyQuery.isLoading
                : audienceTotalsQuery.isLoading,
        [audienceFrequencyQuery.isLoading, audienceTotalsQuery.isLoading, selectedMeasure]
    );

    return {
        error,
        measureValues,
        audienceMeasureValues,
        currentUsersActiveMeasure,
        activeUsersChartQueries,
        audienceChartQueries,
        audiencesDataLoading
    };
}
